import { motion } from "framer-motion";
import { Phone, MessageSquare, ExternalLink, ShieldCheck } from "lucide-react";
import PageContainer from "@/components/PageContainer";
import PageHeader from "@/components/PageHeader";
import { SAFE_REACH_RESOURCES, SAFETY_TIPS } from "@/lib/safeReachData";
import { SAFE } from "@/constants/testIds";

const actionIcon = {
  call: Phone,
  sms: MessageSquare,
  link: ExternalLink,
};

const actionHref = (a) => {
  if (a.type === "call") return `tel:${a.value}`;
  if (a.type === "sms") return `sms:${a.value}`;
  return a.value;
};

export default function SafeReach() {
  return (
    <PageContainer testid={SAFE.root}>
      <PageHeader
        title="Safe Reach"
        subtitle="Independent resources for difficult moments. You stay in control of every next step."
        variant="exit"
        exitTo="/home"
        exitLabel="Exit"
        testid={SAFE.exit}
      />

      {/* Safety banner */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative overflow-hidden rounded-[2rem] p-6 mb-6"
        style={{
          background:
            "linear-gradient(160deg, #4A6B5D 0%, #4D7263 70%, #5E8576 100%)",
          boxShadow: "0 16px 40px rgba(62,92,80,0.22)",
        }}
      >
        <div className="un-grain absolute inset-0" />
        <div className="relative flex items-start gap-3">
          <ShieldCheck size={20} style={{ color: "#FDFBF7" }} className="mt-0.5" />
          <div>
            <p
              className="text-[15px] font-medium leading-snug"
              style={{ color: "#FDFBF7", fontFamily: "Manrope, sans-serif" }}
            >
              If you're in immediate danger, please contact your local
              emergency services. These resources are independent of
              UnAnonymous — you're in control of every next step.
            </p>
            <p
              className="mt-1 text-xs"
              style={{ color: "rgba(253,251,247,0.8)" }}
            >
              You can exit this page any time.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Resources */}
      <div className="flex flex-col gap-3 mb-6">
        {SAFE_REACH_RESOURCES.map((r, idx) => (
          <motion.div
            key={r.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: idx * 0.04 }}
            data-testid={`${SAFE.resourcePrefix}${r.id}`}
            className="un-card p-5"
          >
            <h3
              className="text-[17px] font-medium leading-snug mb-1.5"
              style={{ color: "var(--un-text-1)", fontFamily: "Manrope, sans-serif" }}
            >
              {r.name}
            </h3>
            <p
              className="text-[14px] leading-relaxed mb-3"
              style={{ color: "var(--un-text-2)" }}
            >
              {r.description}
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {r.tags.map((t) => (
                <span
                  key={t}
                  className="px-2.5 py-1 rounded-full text-[10px] font-medium uppercase tracking-wide"
                  style={{
                    background: "rgba(62,92,80,0.10)",
                    color: "var(--un-safe)",
                    fontFamily: "Manrope, sans-serif",
                  }}
                >
                  {t}
                </span>
              ))}
            </div>
            <div className="flex flex-col gap-2">
              {r.actions.map((a) => {
                const Icon = actionIcon[a.type];
                const isPrimary = a.type === "call";
                return (
                  <a
                    key={`${r.id}-${a.label}`}
                    href={actionHref(a)}
                    target={a.type === "link" ? "_blank" : undefined}
                    rel={a.type === "link" ? "noopener noreferrer" : undefined}
                    className="flex items-center justify-between gap-3 px-4 py-3 rounded-2xl un-press"
                    style={{
                      background: isPrimary
                        ? "var(--un-safe)"
                        : "var(--un-bg-3)",
                      color: isPrimary ? "#FDFBF7" : "var(--un-text-1)",
                      fontFamily: "Manrope, sans-serif",
                    }}
                  >
                    <span className="text-[14px] font-medium">{a.label}</span>
                    <Icon size={16} />
                  </a>
                );
              })}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Footer tips */}
      <div
        className="rounded-3xl p-5 mb-8"
        style={{ background: "var(--un-bg-2)" }}
      >
        <ul className="space-y-2">
          {SAFETY_TIPS.map((tip) => (
            <li
              key={tip}
              className="text-xs leading-relaxed flex items-start gap-2"
              style={{ color: "var(--un-text-3)" }}
            >
              <span
                className="mt-1.5 w-1 h-1 rounded-full shrink-0"
                style={{ background: "var(--un-terracotta)" }}
              />
              {tip}
            </li>
          ))}
        </ul>
      </div>
    </PageContainer>
  );
}
